import React, { useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Text } from '@react-three/drei';
import { ChartProps } from '../../types/visualization';
import * as THREE from 'three';

interface Bar3DProps {
  position: [number, number, number];
  height: number;
  color: string;
  label: string;
  value: number;
}

const Bar3D: React.FC<Bar3DProps> = ({ position, height, color, label, value }) => {
  const meshRef = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.1;
    }
  });

  return (
    <group position={position}>
      <mesh ref={meshRef}>
        <boxGeometry args={[0.8, height, 0.8]} />
        <meshStandardMaterial color={color} />
      </mesh>
      <Text
        position={[0, height + 0.5, 0]}
        fontSize={0.3}
        color="black"
        anchorX="center"
        anchorY="middle"
      >
        {value.toLocaleString()}
      </Text>
      <Text
        position={[0, -0.5, 0]}
        fontSize={0.2}
        color="black"
        anchorX="center"
        anchorY="middle"
        rotation={[-Math.PI / 2, 0, 0]}
      >
        {label}
      </Text>
    </group>
  );
};

const BarChart3D: React.FC<ChartProps> = ({ 
  data, 
  config, 
  colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4'] 
}) => {
  if (!data || data.length === 0) {
    return <div>No data available</div>;
  }

  // Get chart configuration
  const xAxisColumn = config?.xAxis?.column || Object.keys(data[0])[0];
  const primaryColumns = config?.yAxis?.primary?.columns || [];

  // Fallback: auto-detect Y column if not configured
  const yColumn = primaryColumns[0] || 
    Object.keys(data[0]).find(k => k !== xAxisColumn && typeof data[0][k] === 'number') || 
    Object.keys(data[0])[1];

  // Normalize data for 3D visualization
  const maxValue = Math.max(...data.map(item => Number(item[yColumn]) || 0));
  const normalizedData = data.map((item, index) => ({
    label: String(item[xAxisColumn]),
    value: Number(item[yColumn]) || 0,
    normalizedHeight: (Number(item[yColumn]) || 0) / maxValue * 5, // Scale to max height of 5
    color: colors[index % colors.length],
    position: [index * 2 - (data.length - 1), 0, 0] as [number, number, number]
  }));

  return (
    <div style={{ width: '100%', height: '500px', background: 'transparent' }}>
      <Canvas camera={{ position: [0, 5, 10], fov: 50 }}>
        <ambientLight intensity={0.4} />
        <directionalLight position={[10, 10, 5]} intensity={1} />
        <pointLight position={[-10, -10, -5]} intensity={0.5} />
        
        {/* Ground plane */}
        <mesh position={[0, -1, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[data.length * 2.5, 8]} />
          <meshStandardMaterial color="#1f2937" transparent opacity={0.3} />
        </mesh>

        {/* 3D Bars */}
        {normalizedData.map((item, index) => (
          <Bar3D
            key={index}
            position={item.position}
            height={item.normalizedHeight}
            color={item.color}
            label={item.label}
            value={item.value}
          />
        ))}

        {/* Title */}
        <Text
          position={[0, 8, 0]}
          fontSize={0.8}
          color="black"
          anchorX="center"
          anchorY="middle"
        >
          {config?.title || '3D Bar Chart'}
        </Text>

        <OrbitControls 
          enablePan={true}
          enableZoom={true}
          enableRotate={true}
          minPolarAngle={Math.PI / 6}
          maxPolarAngle={Math.PI - Math.PI / 6}
        />
      </Canvas>
    </div>
  );
};

export default BarChart3D;
